import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import type { QuotationItemFormRow } from "features/Quotations/utils/quotationItem.utils";
import type {
  PurchaseOrderItem,
  PurchaseOrderLinePayload,
} from "../types/purchase-order.types";

function num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export function poItemOrderedQty(line: PurchaseOrderItem): number {
  return num(line.quantityOrdered ?? line.orderedQty);
}

export function poItemReceivedQty(line: PurchaseOrderItem): number {
  return num(line.quantityReceived ?? line.receivedQty);
}

export function poItemRemainingQty(line: PurchaseOrderItem): number {
  return Math.max(0, poItemOrderedQty(line) - poItemReceivedQty(line));
}

export function poItemRate(line: PurchaseOrderItem): number {
  return num(line.rate ?? line.unitPrice);
}

export function poItemLineTotal(line: PurchaseOrderItem): number {
  if (line.lineTotal != null && Number.isFinite(Number(line.lineTotal))) {
    return Number(line.lineTotal);
  }
  return poItemOrderedQty(line) * poItemRate(line);
}

/** Map saved PO lines into BOQ form rows for the shared line-items field array. */
export function purchaseOrderItemsToBoqRows(
  items: PurchaseOrderItem[] | null | undefined,
): QuotationItemFormRow[] {
  if (!items?.length) return [];
  return items.map((item) => {
    const qty = poItemOrderedQty(item);
    const rate = poItemRate(item);
    const safeQty = qty > 0 ? qty : 1;
    return {
      type: QuotationItemType.MATERIAL,
      description: String(item.description ?? "").trim(),
      quantity: safeQty,
      unit: String(item.unit ?? "PCS").trim() || "PCS",
      rate: Number.isFinite(rate) && rate >= 0 ? rate : 0,
      supplierId: item.supplierId ?? null,
      materialRequestItemId: item.materialRequestItemId ?? null,
    };
  });
}

/** BOQ rows → API payload. Description-only lines. */
export function poBoqRowsToLinePayload(
  rows: QuotationItemFormRow[],
): PurchaseOrderLinePayload[] {
  return rows
    .filter(
      (r) =>
        r.type === QuotationItemType.MATERIAL &&
        typeof r.description === "string" &&
        r.description.trim() !== "",
    )
    .map((r) => ({
      supplierId: r.supplierId ?? null,
      materialRequestItemId: r.materialRequestItemId ?? null,
      description: String(r.description ?? "").trim() || null,
      quantityOrdered: Number(r.quantity),
      unit: String(r.unit ?? "").trim() || "PCS",
      rate: Number(r.rate),
    }));
}
